import { Map, Record } from 'immutable';

import { FullBetSlipState } from '../../types';
import type { Logout } from 'app/lib/auth/types';

import { logout } from 'app/redux/actions/auth';
import type { InitAction } from 'app/redux/actions/init';

import { CLEANUP, Cleanup, REFRESH_AVAILABILITY, RefreshAvailability } from '../../actions';
import {
  ACCEPT_PRICE_CHANGES, AcceptPriceChanges,
  COMPLETE_REPLACE, CompleteReplace,
  DELETE_FROM_BET_SLIP, DeleteFromBetSlip,
  INSERT_IN_BET_SLIP, InsertInBetSlip,
} from '../../actions/entries';
import {
  REFRESH_MATCH_PRICES, RefreshMatchPrices,
  REMOVE_EXPRESS_FREE_BET, RemoveExpressFreeBet,
  SET_EXPRESS_FREE_BET, SetExpressFreeBet,
} from '../../actions/stakeTypes/express';
import {
  SUBMIT_STAKE_FAILURE, SUBMIT_STAKE_REQUEST, SUBMIT_STAKE_SUCCESS, SubmitStakeFailure,
  SubmitStakeRequest,
  SubmitStakeSuccess,
} from '../../actions/submission';
import { AllowingPricePreference, BetErrorCode, MatchErrorCode, SubmissionState } from '../../constants';
import { EntryLimits, PaymentOptionsEntry } from '../../records';
import { ExpressStakeTypeState, RecordOfExpressStakeType } from '../../types/stakeTypes/express';

// ---

export const initialState = Record<ExpressStakeTypeState>({
  submission: SubmissionState.AWAITING,
  limits: EntryLimits(),
  matchPrices: Map(),
  freeBet: null,
  paymentOptions: PaymentOptionsEntry(),
}, 'expressStakeType');

export type ExpressStakeTypeActions =
  | InitAction
  | InsertInBetSlip
  | DeleteFromBetSlip
  | CompleteReplace
  | RefreshAvailability
  | RefreshMatchPrices
  | SubmitStakeRequest
  | SubmitStakeSuccess
  | SubmitStakeFailure
  | SetExpressFreeBet
  | RemoveExpressFreeBet
  | AcceptPriceChanges
  | Cleanup
  | Logout;

// ---

function updateStateAfterFailure(
  state: RecordOfExpressStakeType,
  payload: SubmitStakeFailure['payload'],
): RecordOfExpressStakeType {
  let matchPrices = state.get('matchPrices');

  const matchIds = new Set<number>();

  // Экспресс для одинара
  payload?.betErrors?.forEach((bet) => {
    if (bet.code === BetErrorCode.PRICE_CHANGED && bet.newPrice) {
      const entryPrice = matchPrices.get(bet.matchId)?.entryPrice;
      if (typeof entryPrice !== 'number') {
        console.error('BetSlip > reducer > stakeTypes > express > updateStateAfterFailure: `entryPrice` not found');
        return;
      }
      if (matchIds.has(bet.matchId)) {
        // Кэфы могут поменяться отдельно у исходов. Если этот исход был в составе ЭВМ,
        // тогда мы сбрасываем кэф к начальному значению. И ждем новый кэф с бэка (смотреть сагу)
        const entityPrice = state.get('matchPrices').get(bet.matchId)?.entityPrice;
        matchPrices = matchPrices.set(bet.matchId, {
          entryPrice,
          entityPrice,
        });
      }
      else {
        matchIds.add(bet.matchId);
        matchPrices = matchPrices.set(bet.matchId, {
          entryPrice,
          entityPrice: bet.newPrice,
        });
      }
    }
  });

  // Экспресс внутри матча
  payload?.matchErrors?.forEach((match) => {
    if (match.code === MatchErrorCode.PRICE_CHANGED && match.newPrice) {
      const entryPrice = matchPrices.get(match.matchId)?.entryPrice;
      if (typeof entryPrice !== 'number') {
        console.error('BetSlip > reducer > stakeTypes > express > updateStateAfterFailure: `entryPrice` not found');
        return;
      }
      matchPrices = matchPrices.set(match.matchId, {
        entryPrice,
        entityPrice: match.newPrice,
      });
    }
  });

  return state.update('matchPrices', () => matchPrices);
}

function acceptPriceChanges(
  state: RecordOfExpressStakeType,
  allowPricePreference: AllowingPricePreference,
): RecordOfExpressStakeType {
  return state.update('matchPrices', (matchPrices) => (
    matchPrices.map((prices) => {
      const { entityPrice, entryPrice } = prices;

      if (!entityPrice || !entryPrice) {
        return prices;
      }

      if (entityPrice === entryPrice) {
        return prices; // price doesn't changed
      }

      const isIncrease = allowPricePreference !== AllowingPricePreference.NOTHING
        && entityPrice > entryPrice;
      const isDecrease = allowPricePreference === AllowingPricePreference.EVERYTHING
        && entityPrice < entryPrice;

      if (isIncrease || isDecrease) {
        return {
          ...prices,
          entryPrice: entityPrice,
        };
      }

      return prices;
    })
  ));
}

// ---

export default function (
  state = initialState(),
  action: ExpressStakeTypeActions,
  globalState: FullBetSlipState,
): RecordOfExpressStakeType {
  switch (action.type) {
    case INSERT_IN_BET_SLIP:
    case DELETE_FROM_BET_SLIP:
    case COMPLETE_REPLACE: {
      const { express } = action.payload;

      if (!express) {
        return state;
      }

      return state.merge({
        limits: express.limits,
        paymentOptions: express.paymentOptions,
        matchPrices: express.matchPrices,
        submission: SubmissionState.AWAITING,
      });
    }

    case REFRESH_AVAILABILITY: {
      const { express } = action.payload;

      if (!express) {
        return state;
      }

      return state.merge({
        limits: express.limits,
        paymentOptions: express.paymentOptions,
      });
    }

    case REFRESH_MATCH_PRICES:
      return state.merge({
        matchPrices: action.payload,
        submission: SubmissionState.AWAITING,
      });

    case SUBMIT_STAKE_REQUEST:
      return state.update('submission', () => SubmissionState.PROCESSING);

    case SUBMIT_STAKE_SUCCESS: {
      if (action.payload.isBigStake) {
        return state;
      }

      return state.update('submission', () => SubmissionState.ACCEPTED);
    }

    case SUBMIT_STAKE_FAILURE: {
      if (action.payload.isBigStake) {
        return state;
      }

      state = state.update('submission', () => SubmissionState.FAILED);
      state = updateStateAfterFailure(state, action.payload);

      const { allowPricePreference } = globalState?.preferences || {};

      if (allowPricePreference) {
        state = acceptPriceChanges(state, allowPricePreference);
      }

      return state;
    }

    case ACCEPT_PRICE_CHANGES: {
      return acceptPriceChanges(state, AllowingPricePreference.EVERYTHING);
    }

    case SET_EXPRESS_FREE_BET:
      return state.update('freeBet', () => action.payload.entry);

    case REMOVE_EXPRESS_FREE_BET:
    case logout.pending.toString() : {
      return state.delete('freeBet');
    }

    case CLEANUP:
      return initialState();

    default:
      return state;
  }
}
